import { capitalize } from "@mui/material"
import { pluralize } from "utils/stringUtils"

import { NotificationType } from "@/schemas/notification"
import { useInstruction } from "@/screens/ManageProducts/components/Instructions/hooks/useInstruction"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseInstructionActionsReturn {
  addProductsToInstruction: (args: { instructionId: string; productIds: number[] }) => Promise<boolean>
  removeProductsFromInstruction: (args: { instructionId: string; productIds: number[] }) => Promise<boolean>
  updateInstructionStatus: (args: { instructionId: string; isActive: boolean }) => Promise<boolean>
}
const useInstructionActions = (): UseInstructionActionsReturn => {
  const { addProducts, removeProducts, updateInstruction } = useInstruction()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const addProductsToInstruction = async ({
    instructionId,
    productIds,
  }: {
    instructionId: string
    productIds: number[]
  }) => {
    const { error, data } = await addProducts({
      input: { instructionId, productIds },
    })

    const name = pluralize(productIds.length, "product", "products")
    const hasError =
      error?.message ||
      (data?.instruction.batchAddInstructionProducts.__typename === "InstructionFailure" &&
        Boolean(data?.instruction.batchAddInstructionProducts.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? `Failed to add ${name} to instruction`,
      })
      return false
    }
    return true
  }

  const removeProductsFromInstruction = async ({
    instructionId,
    productIds,
  }: {
    instructionId: string
    productIds: number[]
  }) => {
    const { error, data } = await removeProducts({
      input: { instructionId, productIds },
    })

    const name = pluralize(productIds.length, "product", "products")

    const hasError =
      error?.message ||
      (data?.instruction.batchDeleteInstructionProducts.__typename === "InstructionFailure" &&
        Boolean(data?.instruction.batchDeleteInstructionProducts.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? `Failed to remove ${name} from instruction`,
      })
      return false
    } else {
      notify({
        type: NotificationType.Success,
        silent: false,
        message: error?.message ?? `${capitalize(name)} Successfully removed`,
      })
      return true
    }
  }

  const updateInstructionStatus = async ({ instructionId, isActive }: { instructionId: string; isActive: boolean }) => {
    const { error, data } = await updateInstruction({
      input: {
        instructionId,
        isActive,
      },
    })
    const hasError =
      error?.message ||
      (data?.instruction.update.__typename === "InstructionFailure" && Boolean(data?.instruction.update.error))

    if (hasError) {
      notify({
        message: isActive ? "Failed Instruction Activation" : "Failed Instruction Deactivation",
        type: NotificationType.Error,
      })
      return false
    } else {
      notify({
        message: isActive
          ? "Instruction Successfully Activated"
          : "Product Instruction has been successfully deactivated",
        type: NotificationType.Success,
      })
      return true
    }
  }

  return {
    addProductsToInstruction,
    removeProductsFromInstruction,
    updateInstructionStatus,
  }
}

export default useInstructionActions
